import React from 'react'
import styled from 'styled-components'
import { Toggle } from 'react-powerplug'

import Form from 'src/components/Form.js'
import Logo from 'src/components/Logo.js'
import Sharing from 'src/components/Sharing.js'
import SiteFooter from 'src/components/SiteFooter.js'
import TopBar from 'src/components/TopBar.js'
import { BLACK, GRAY, MINT, RED, WHITE } from 'src/styles/colors.js'
import { Row } from 'src/styles/elements.js'
import injectGlobalStyles from 'src/styles/global-styles.js'

injectGlobalStyles()

const AppWrapper = styled.div``

const MainContent = styled(Row)`
  padding-bottom: 16px;
`

const ContentLogo = styled.div`
  float: right;
  width: 240px;
  height: 240px;
  margin: 16px 0 32px 32px;

  @media (max-width: 768px) {
    float: none;
    width: 160px;
    height: 160px;
    margin: 32px auto;
  }
`

const Heading = styled.h1`
  margin: 0 0 24px;
  padding-top: 8px;
  color: ${RED};
  font-family: 'Asap', sans-serif;
  font-size: 52px;
  line-height: 56px;
  text-transform: uppercase;
`

const PromiseContents = styled.div`
  color: ${BLACK};
`

const Highlight = styled.span`
  color: ${RED};
`

const WhatNextWrapper = styled.div`
  margin-top: 24px;
  font-size: 14px;
  line-height: 20px;
  color: ${GRAY};
`

const WhatNextToggle = styled.a`
  color: ${props => (props.isActive ? BLACK : GRAY)};
  font-size: 16px;
  font-weight: ${props => (props.isActive ? 'bold' : 'normal')};
  line-height: 24px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  svg {
    vertical-align: middle;
    margin-left: 8px;
  }
`

const CallToAction = styled.div`
  clear: both;
  margin: 64px 0 16px;
  color: ${RED};
  font-size: 24px;
  font-weight: bold;
`

const SharingWrapper = styled.div`
  margin-top: 24px;
`

const ParticipantFeed = styled.div`
  margin-top: 48px;
  padding: 56px 0 56px;
  background: #fefefe;
  color: ${BLACK};
`

const ParticipantFeedHeading = styled.h2`
  margin: 0 0 32px;
  color: ${MINT};
  font-size: 32px;
  line-height: 32px;
`

const ParticipantFeedGrid = styled.div`
  display: flex;
  margin: 0 -16px;

  > * {
    margin-bottom: 16px;
    padding: 0 16px;
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;

    > * {
      width: 100%;
    }
  }
`

const ParticipantFeedInstructions = styled.ol`
  margin: 0;
  padding: 0 0 0 24px;

  li {
    margin-bottom: 8px;
  }
`

const ParticipantPhoto = styled.img`
  width: auto;
  height: 182px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
  }
`

const AboutSection = styled.div`
  padding: 64px 0 80px;
  background: ${WHITE};
`

const AboutHeading = styled.h2`
  margin: 0 0 32px;
  color: ${MINT};
  font-size: 32px;
  line-height: 32px;
`

/**
 * App
 */
const App = () => (
  <AppWrapper>
    <TopBar />

    <MainContent>
      <ContentLogo>
        <Logo color={RED} />
      </ContentLogo>

      <Heading>Det sociala löftet</Heading>

      <PromiseContents>
        <p>
          Vi tar ställning för <Highlight>frihet och jämlikhet</Highlight>.
          <br />
          Vi accepterar inte att någon annan människas eller grupps frihet ska
          tas ifrån dem till förmån för någon annans.
        </p>
        <p>
          Vi lovar varandra detta i den djupa insikten att om vi alla håller
          detta löfte så kommer ingen kunna ställa våra kamper mot varandra och
          vi kommer tillsammans <Highlight>förändra samhället</Highlight>.
        </p>
      </PromiseContents>

      <Toggle>
        {({ on, toggle }) =>
          on === true ? (
            <div>
              <CallToAction>Sprid ordet!</CallToAction>
              <p>
                Du är inte ensam. Ge ännu fler möjligheten att hitta nya eller
                fler vägar till en ljusare framtid.
                <br />
                Sprid ordet om Det sociala löftet!
              </p>

              <SharingWrapper>
                <Sharing />
              </SharingWrapper>
            </div>
          ) : (
            <div>
              <CallToAction>Skriv under, var med!</CallToAction>

              <Form onSign={toggle} />

              <Toggle>
                {({ on: isActive, toggle: toggleActive }) => (
                  <WhatNextWrapper>
                    <WhatNextToggle
                      href="#"
                      isActive={isActive}
                      onClick={evt => {
                        evt.preventDefault()
                        toggleActive()
                      }}
                    >
                      Vad händer sen?
                    </WhatNextToggle>
                    {isActive && (
                      <p>
                        Vi kommer att mejla dig information om hur du kan
                        engagera dig i Tillsammansskapet, och andra sätt ni kan
                        organisera ert lokalsamhälle på. Vi kommer att driva
                        lokala frågor som gör livet bättre för oss som lever
                        där, välkommen att vara med! Dina uppgifter sparas i
                        enlighet med GDPR och vi kommer aldrig lämna dem vidare
                        till tredje part.
                      </p>
                    )}
                  </WhatNextWrapper>
                )}
              </Toggle>
            </div>
          )
        }
      </Toggle>
    </MainContent>

    <ParticipantFeed>
      <Row>
        <ParticipantFeedHeading>Visa och dela!</ParticipantFeedHeading>

        <ParticipantFeedGrid>
          <div>
            <ParticipantFeedInstructions>
              <li>Du som vill kan ta en bild med handen på hjärtat</li>
              <li>
                Dela den i sociala medier tillsammans med länken till denna
                sida, och hashtaggen <strong>#detsocialalöftet</strong>.
              </li>
            </ParticipantFeedInstructions>
          </div>
          <div>
            <ParticipantPhoto src="/img/IMG_1277-small.jpg" alt="" />
          </div>
          <div>
            <ParticipantPhoto src="/img/IMG_1278-small.jpg" alt="" />
          </div>
          <div>
            <ParticipantPhoto src="/img/IMG_1279-small.jpg" alt="" />
          </div>
        </ParticipantFeedGrid>
      </Row>
    </ParticipantFeed>

    <AboutSection>
      <Row>
        <AboutHeading>Förbundet Tillsammansskapet</AboutHeading>

        <p>
          Förbundet Tillsammansskapet verkar för en rättvis och jämlik värld,
          där människor lever tillsammans i frihet och på lika villkor. Det gör
          vi genom organisering. Organisering är att människor, demokratiskt,
          bygger jämlika relationer, och på så vis tillsammans stöttar varandra
          i vardagen, samt formulerar politiska krav och lösningar.
        </p>
        <p>
          Tillsammansskapet samlar föreningar, nätverk och aktivistgrupper, som
          genom organisering bygger sammanhållning och utmanar sociala och
          ekonomiska orättvisor i lokalsamhället. Förbundet Tillsammansskapets
          syfte är att stödja medlemsgruppernas organisering, och sprida
          organisering till lokalsamhällen där rörelsen ännu inte är etablerad.
          Förbundet Tillsammansskapet är religiöst och partipolitiskt obundet.
        </p>
        <p>
          Vi består av vanligt folk i lokalsamhällen från Burlöv i söder till
          Luleå i norr. Besök gärna får sida på Facebook för dagliga
          uppdateringar.
        </p>
      </Row>
    </AboutSection>

    <SiteFooter />
  </AppWrapper>
)

export default App
